import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { camelizeKeys } from '../../../../../utils/camelizeKeys';
import { ChevronDown, ChevronUp, RefreshCw, LockKeyhole, UnlockKeyhole } from 'lucide-react';
import Title from './investmentFunds/Title';
import Info from './investmentFunds/Info';
import { formatCurrency, formatPercentage } from '../../../../../utils/formatters';
import SortableHeader from '../../../../../components/ui/table/SortableHeader';
import CustomCheckbox from '../../../../../components/ui/checkboxes/CustomCheckbox';
import { cn } from '../../../../../utils/classnames';
import BalancingBadge from '../../../../../components/ui/badges/BalancingBadge';
import CurrencyInput from '../../../../../components/ui/inputs/CurrencyInput';
import CustomButton from '../../../../../components/ui/buttons/CustomButton';
import { useToast } from '../../../../../components/ui/toast/ToastContext';
import Tooltip from '../../../../../components/ui/tooltips/Tooltip';
import PendingSettlement from '../../../../../components/ui/tooltips/PendingSettlement';
import { fetchWithCsrf } from '../../../../../utils/fetchWithCsrf';

const BASE_GRID = '30px 1fr 200px 200px 170px 270px';
const BASE_SUMMARY_GRID = '30px 1fr 230px 170px 230px 170px';
const COMPLEMENT_GRID = '30px 1fr 200px 200px 170px 270px';

const getPositionId = (position) => String(position.id);

const getAllFundSelectableIds = (positions = []) => {
  return positions.flatMap((position) => [
    getPositionId(position),
    ...(position.complementaryPositions || []).map((comp) => getPositionId(comp)),
  ]);
};

const isFundLocked = (item) => {
  return Boolean(item.locked || item.isLocked || item.fundLocked);
};

const normalizeSortableText = (value) => {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
};

const getSortableValue = (position, field) => {
  switch (field) {
    case 'name':
      return normalizeSortableText(position.fund && position.fund.displayName);

    case 'pl':
      return Number(position.amount) || 0;

    case 'idealAmount':
      return Number(position.idealAmount) || 0;

    case 'balancing':
      return Number(position.balancing) || 0;

    default: {
      const value = position[field];

      if (typeof value === 'number') {
        return value;
      }

      return normalizeSortableText(value);
    }
  }
};

const sortInvestmentFundPositions = (positions = [], sortBy, sortOrder) => {
  const direction = sortOrder === 'desc' ? -1 : 1;

  return [...positions]
    .map((position, index) => ({ position, index }))
    .sort((a, b) => {
      const valueA = getSortableValue(a.position, sortBy);
      const valueB = getSortableValue(b.position, sortBy);

      let result = 0;

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        result = valueA - valueB;
      } else {
        result = String(valueA).localeCompare(String(valueB), 'pt-BR', {
          numeric: true,
          sensitivity: 'base',
        });
      }

      if (result === 0) {
        return a.index - b.index;
      }

      return result * direction;
    })
    .map(({ position }) => position);
};

const getInitialLockedFundIds = (positions = []) => {
  return positions.flatMap((position) => {
    const ids = [];

    if (isFundLocked(position)) {
      ids.push(getPositionId(position));
    }

    (position.complementaryPositions || []).forEach((comp) => {
      if (isFundLocked(comp)) {
        ids.push(getPositionId(comp));
      }
    });

    return ids;
  });
};

const sumMoney = (value1, value2) => {
  const value1InCents = Math.round((Number(value1) || 0) * 100);
  const value2InCents = Math.round((Number(value2) || 0) * 100);

  return (value1InCents + value2InCents) / 100;
};

const getBalancingSuggestionTotals = (positions = []) => {
  return positions.reduce(
    (totals, position) => {
      const items = [position, ...(position.complementaryPositions || [])];

      items.forEach((item) => {
        const balancing = Number(item.balancing) || 0;

        if (balancing < 0) {
          totals.totalSuggestedSell = sumMoney(totals.totalSuggestedSell, balancing);
        }

        if (balancing > 0) {
          totals.totalSuggestedBuy = sumMoney(totals.totalSuggestedBuy, balancing);
        }
      });

      return totals;
    },
    {
      totalSuggestedSell: 0,
      totalSuggestedBuy: 0,
    }
  );
};

const getPositiveInputsSum = (inputValues = {}, currentId = null) => {
  return Object.entries(inputValues).reduce((sum, [id, value]) => {
    if (String(id) === String(currentId)) return sum;

    const numericValue = Number(value) || 0;

    if (numericValue <= 0) return sum;

    return sumMoney(sum, numericValue);
  }, 0);
};

const getButtonAdjustedValue = ({
  value,
  currentId,
  inputValues,
  walletBalance,
  typing = false,
  max = false,
  fullSell = false,
}) => {
  let numericValue = max ? Number.POSITIVE_INFINITY : Number(value) || 0;

  if (fullSell) {
    numericValue *= -1;
  }

  if (numericValue < 0) {
    return {
      value: numericValue,
      valid: true,
      message: null,
    };
  }

  const totalBalance = Number(walletBalance) || 0;
  const buysSum = getPositiveInputsSum(inputValues, currentId);

  if (sumMoney(buysSum, numericValue) > totalBalance) {
    const readjustedValue = sumMoney(totalBalance, -buysSum);

    if (typing) {
      return {
        value: numericValue,
        valid: false,
        message: {
          type: 'error',
          text: 'Valor ultrapassa o saldo.',
        },
      };
    }

    if (readjustedValue > 0) {
      return {
        value: readjustedValue,
        valid: true,
        message: {
          type: 'warning',
          text: max ? 'Saldo restante aplicado.' : 'Valor reajustado ao saldo.',
        },
      };
    }

    return {
      value: 0,
      valid: false,
      message: {
        type: 'error',
        text: 'Valor ultrapassa o saldo.',
      },
    };
  }

  return {
    value: numericValue,
    valid: true,
    message: null,
  };
};

const XLSX_HEADERS = [
  'conta',
  'codigo_fundo',
  'valor_operacao',
  'tipo_operacao',
  'resgate_total',
  'data_operacao',
  'alavancagem_btg',
  'pos_horario',
  'resgate_fmp',
  'modalidade_resgate_fmp',
];

const getAllExportablePositions = (positions = []) => {
  return positions.flatMap((position) => [position, ...(position.complementaryPositions || [])]);
};

const buildXlsxRows = ({ positions = [], balanceInputValues = {}, accountNumber }) => {
  const rows = [XLSX_HEADERS];

  const currentDate = new Date();
  const operationDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());

  getAllExportablePositions(positions).forEach((item) => {
    const inputValue = Number(balanceInputValues[String(item.id)]) || 0;
    const operationValue = Math.abs(inputValue);

    if (operationValue === 0 || Number.isNaN(operationValue)) return;

    const operationType = inputValue < 0 ? 'R' : 'A';
    const investedValue = Number(item.amount) || 0;
    const isFullSell = operationType === 'R' && Number(operationValue.toFixed(2)) === Number(investedValue.toFixed(2));

    rows.push([
      accountNumber || '',
      item.fund && item.fund.cgeCode ? item.fund.cgeCode : '',
      operationValue,
      operationType,
      isFullSell ? 'S' : 'N',
      operationDate,
      'N',
      'N',
      'N',
      null,
    ]);
  });

  return rows;
};

const applyWorksheetFormats = (worksheet) => {
  if (!worksheet['!data']) return;

  for (let i = 1; i < worksheet['!data'].length; i += 1) {
    if (worksheet['!data'][i][2]) {
      worksheet['!data'][i][2].z = '#,##0.00';
    }

    if (worksheet['!data'][i][5]) {
      worksheet['!data'][i][5].z = 'dd/mm/yyyy';
    }
  }
};

const applyWorksheetColumnWidths = (worksheet) => {
  if (!worksheet['!data'] || !worksheet['!data'][0]) return;

  const widthsArray = [];

  for (let i = 0; i < worksheet['!data'][0].length; i += 1) {
    let maxWidth = 0;

    for (let j = 0; j < worksheet['!data'].length; j += 1) {
      const cell = worksheet['!data'][j][i];
      const cellValue = cell ? cell.v : '';
      const cellWidth = cellValue ? String(cellValue).length : 0;

      if (cellWidth > maxWidth) {
        maxWidth = cellWidth;
      }
    }

    widthsArray.push({ wch: maxWidth + 1 });
  }

  worksheet['!cols'] = widthsArray;
};

const exportInvestmentFundsXlsx = ({ positions, balanceInputValues, accountNumber, fileName }) => {
  const rows = buildXlsxRows({
    positions,
    balanceInputValues,
    accountNumber,
  });

  if (rows.length === 1) {
    return {
      exported: false,
      message: 'Nenhum ajuste preenchido para exportar.',
    };
  }

  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet(rows, { dense: true });

  applyWorksheetFormats(worksheet);
  applyWorksheetColumnWidths(worksheet);

  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

  XLSX.writeFile(workbook, fileName);

  return {
    exported: true,
    message: 'Planilha exportada com sucesso.',
  };
};

const LockIconButton = ({ locked, disabled = false, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={locked ? 'Fundo trancado' : 'Fundo destrancado'}
      className={cn(
        'flex-shrink-0 flex items-center gap-1.5 rounded-full transition-opacity',
        disabled ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80'
      )}
    >
      <span
        className={cn(
          'flex items-center justify-center transition-colors',
          locked ? 'text-redSoft' : 'text-graySemiDark'
        )}
      >
        {locked ? <LockKeyhole size={14} strokeWidth={2.5} /> : <UnlockKeyhole size={14} strokeWidth={2.3} />}
      </span>

      <span
        className={cn(
          'relative inline-flex h-4 w-8 flex-shrink-0 rounded-full transition-colors duration-200 ease-in-out',
          locked ? 'bg-redExtraLight' : 'bg-bluePale'
        )}
      >
        <span
          className={cn(
            'absolute left-0 top-1/2 h-5 w-5 rounded-full border shadow-sm transition-transform duration-200 ease-in-out',
            locked ? 'bg-redSoft border-redSoft' : 'bg-white border-bluePale'
          )}
          style={{
            transform: locked ? 'translate(13px, -50%)' : 'translate(0px, -50%)',
          }}
        />
      </span>
    </button>
  );
};

const InvestmentFundRow = ({
  position,
  idx,
  selectedFundIds,
  balanceInputValues,
  lockedFundIds,
  lockingFundIds,
  onToggleFundSelection,
  onBalanceInputChange,
  onAddBalancing,
  onMaxBalancing,
  onFullSell,
  onToggleFundLock,
  situationColor,
  percentageTextColor,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const hasComplements = position.complementaryPositions && position.complementaryPositions.length > 0;

  const isBlueRow = idx % 2 === 0;
  const fundChecked = selectedFundIds.includes(String(position.id));
  const positionLocked = lockedFundIds.includes(String(position.id));
  const positionLocking = lockingFundIds.includes(String(position.id));

  return (
    <div className={`${isBlueRow ? 'bg-blueLight' : 'bg-white'} rounded-xl`}>
      <div
        className="grid items-center gap-4 px-3 py-3 text-sm text-blueDarker"
        style={{ gridTemplateColumns: BASE_GRID }}
      >
        <div className="truncate">
          <CustomCheckbox checked={fundChecked} onChange={() => onToggleFundSelection(position.id)} />
        </div>

        <div
          className={`truncate font-semibold flex items-center gap-4 min-w-0 ${situationColor(position.situation)} ${
            hasComplements ? 'cursor-pointer hover:opacity-80' : ''
          }`}
          title={position.fund.displayName}
          onClick={() => hasComplements && setIsExpanded((prev) => !prev)}
        >
          <LockIconButton
            locked={positionLocked}
            disabled={positionLocking}
            onClick={(e) => {
              e.stopPropagation();
              onToggleFundLock(position);
            }}
          />

          <span className="truncate">{position.fund.displayName}</span>

          {hasComplements && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded((prev) => !prev);
              }}
              className="flex-shrink-0 w-5 h-5 rounded-full bg-white/70 border border-bluePale flex items-center justify-center hover:bg-white transition-colors"
            >
              {isExpanded ? (
                <ChevronUp size={12} className="text-blueMedium transition-transform duration-300" strokeWidth={2} />
              ) : (
                <ChevronDown size={12} className="text-blueMedium transition-transform duration-300" strokeWidth={2} />
              )}
            </button>
          )}
        </div>

        <div className="flex gap-2 flex-row items-center overflow-visible min-w-0" title={position.amount}>
          <div className="truncate">
            <span>
              {formatCurrency(position.amount)}
              <span className={cn('font-semibold', percentageTextColor(position.balancing))}>
                {' '}
                ({formatPercentage(position.amountPercentage)})
              </span>
            </span>
          </div>

          {position.hasPendingSettlements && (
            <span className="flex-shrink-0 overflow-visible">
              <PendingSettlement content={`Em trânsito: ${formatCurrency(position.pendingSettlementsValue)}`} />
            </span>
          )}
        </div>

        <div className="truncate">
          <span>
            {formatCurrency(position.idealAmount)}
            <span className="font-semibold"> ({formatPercentage(position.idealPercentage)})</span>
          </span>
        </div>

        <div className="flex items-center gap-2 min-w-0">
          <BalancingBadge value={position.balancing} />
        </div>

        <div className="truncate flex flex-row gap-2 items-center">
          <CurrencyInput
            name={`balancing-${position.id}`}
            value={balanceInputValues[String(position.id)] || 0}
            onChange={(e) => onBalanceInputChange(position.id, e.target.value)}
            py="py-2"
            textSize="text-sm"
            allowNegative
          />

          <button
            type="button"
            onClick={() => onAddBalancing(position)}
            className="bg-tealMedium hover:opacity-90 transition-opacity text-white font-semibold rounded-xl px-2 pt-3 pb-2 text-xs border-r-2 border-b-2 border-tealBase"
          >
            +
          </button>

          <button
            type="button"
            onClick={() => onMaxBalancing(position)}
            className="bg-tealMedium hover:opacity-90 transition-opacity text-white font-semibold rounded-xl px-2 pt-3 pb-2 text-xs border-r-2 border-b-2 border-tealBase"
          >
            MAX
          </button>

          <button
            type="button"
            onClick={() => onFullSell(position)}
            className="bg-tealMedium hover:opacity-90 transition-opacity text-white font-semibold rounded-xl px-2 pt-3 pb-2 text-xs border-r-2 border-b-2 border-tealBase"
          >
            RT
          </button>
        </div>
      </div>

      {hasComplements && (
        <div
          className={`grid transition-all duration-300 ease-in-out ${isExpanded ? 'opacity-100' : 'opacity-0'}`}
          style={{ gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
        >
          <div className="overflow-hidden">
            <hr className="border-bluePale" />

            <div className="pb-2">
              {position.complementaryPositions.map((comp) => {
                const compFundChecked = selectedFundIds.includes(String(comp.id));
                const compLocked = lockedFundIds.includes(String(comp.id));
                const compLocking = lockingFundIds.includes(String(comp.id));

                return (
                  <div
                    key={comp.id}
                    className="grid items-center gap-4 px-3 py-2 text-sm text-blueDarker"
                    style={{ gridTemplateColumns: COMPLEMENT_GRID }}
                  >
                    <div className="truncate">
                      <CustomCheckbox checked={compFundChecked} onChange={() => onToggleFundSelection(comp.id)} />
                    </div>

                    <div
                      className={`truncate font-semibold flex items-center gap-4 min-w-0 ${situationColor(
                        comp.situation
                      )}`}
                      title={comp.fund && comp.fund.displayName}
                    >
                      <LockIconButton
                        locked={compLocked}
                        disabled={compLocking}
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleFundLock(comp);
                        }}
                      />

                      <span className="truncate">{comp.fund && comp.fund.displayName}</span>
                    </div>

                    <div className="flex gap-2 flex-row items-center overflow-visible min-w-0" title={position.amount}>
                      <div className="truncate">
                        <span>
                          {formatCurrency(comp.amount)}
                          <span className={cn('font-semibold', percentageTextColor(comp.balancing))}>
                            {' '}
                            ({formatPercentage(comp.amountPercentage)})
                          </span>
                        </span>
                      </div>

                      {comp.hasPendingSettlements && (
                        <span className="flex-shrink-0 overflow-visible">
                          <PendingSettlement content={`Em trânsito: ${formatCurrency(comp.pendingSettlementsValue)}`} />
                        </span>
                      )}
                    </div>

                    <div className="truncate">
                      <span>
                        {formatCurrency(comp.idealAmount)}
                        <span className="font-semibold"> ({formatPercentage(comp.idealPercentage)})</span>
                      </span>
                    </div>

                    <div className="flex items-center gap-2 min-w-0">
                      <BalancingBadge value={comp.balancing} />
                    </div>

                    <div className="truncate flex flex-row gap-2 items-center">
                      <CurrencyInput
                        name={`balancing-${comp.id}`}
                        value={balanceInputValues[String(comp.id)] || 0}
                        onChange={(e) => onBalanceInputChange(comp.id, e.target.value)}
                        py="py-2"
                        textSize="text-sm"
                        allowNegative
                      />

                      <button
                        type="button"
                        onClick={() => onAddBalancing(comp)}
                        className="bg-tealMedium hover:opacity-90 transition-opacity text-white font-semibold rounded-xl px-2 pt-3 pb-2 text-xs border-r-2 border-b-2 border-tealBase"
                      >
                        +
                      </button>

                      <button
                        type="button"
                        onClick={() => onMaxBalancing(comp)}
                        className="bg-tealMedium hover:opacity-90 transition-opacity text-white font-semibold rounded-xl px-2 pt-3 pb-2 text-xs border-r-2 border-b-2 border-tealBase"
                      >
                        MAX
                      </button>

                      <button
                        type="button"
                        onClick={() => onFullSell(comp)}
                        className="bg-tealMedium hover:opacity-90 transition-opacity text-white font-semibold rounded-xl px-2 pt-3 pb-2 text-xs border-r-2 border-b-2 border-tealBase"
                      >
                        RT
                      </button>
                    </div>
                  </div>
                );
              })}

              <div
                className="grid items-center gap-4 px-3 py-2 text-sm text-blueDarker"
                style={{ gridTemplateColumns: COMPLEMENT_GRID }}
              >
                <div />
                <div className="pl-5 text-sm font-semibold uppercase text-right">Total:</div>

                <div className="truncate font-semibold">
                  {formatCurrency(position.amountWithComplements)}
                  <span className="ml-1">({formatPercentage(position.amountWithComplementsPercentage)})</span>
                </div>

                <div />
                <div />
                <div />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InvestmentFunds = ({ apiClientId, onPositionUpdated }) => {
  const { addToast } = useToast();

  const [data, setData] = useState({
    investmentFundPositionsCount: 0,
    totalInFunds: 0,
    idealInFunds: 0,
    totalInFundsPercentage: 0,
    idealInFundsPercentage: 0,
    pendingSettlements: 0,
    investmentFundPositions: [],
    freeInvestmentPl: 0,
    walletBalance: 0,
    accountNumber: '',
    clientName: '',
  });

  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedFundIds, setSelectedFundIds] = useState([]);
  const [lockedFundIds, setLockedFundIds] = useState([]);
  const [lockingFundIds, setLockingFundIds] = useState([]);
  const [balanceInputValues, setBalanceInputValues] = useState({});

  const lockingFundIdsRef = useRef(new Set());

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const fetchData = () => {
    fetch(`/app/analyst/client/${apiClientId}/dashboard/investment_funds`)
      .then((res) => res.json())
      .then((responseData) => {
        const parsedData = camelizeKeys(responseData) || {};
        const positions = parsedData && parsedData.investmentFundPositions ? parsedData.investmentFundPositions : [];

        setData({
          ...parsedData,
          investmentFundPositions: positions,
        });

        setSelectedFundIds(getAllFundSelectableIds(positions));
        setLockedFundIds(getInitialLockedFundIds(positions));
        setBalanceInputValues({});
      })
      .catch((err) => {
        console.error('Erro ao carregar dados', err);
        addToast({ type: 'error', message: 'Erro ao carregar dados' });
      });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const sortedInvestmentFundPositions = useMemo(() => {
    return sortInvestmentFundPositions(data.investmentFundPositions || [], sortBy, sortOrder);
  }, [data.investmentFundPositions, sortBy, sortOrder]);

  const totalInFundsPercentage = data.totalInFundsPercentage || 0;
  const idealInFundsPercentage = data.idealInFundsPercentage || 0;
  const totalMatchesIdeal = totalInFundsPercentage === idealInFundsPercentage;

  const situationColor = (situation) => {
    switch (situation) {
      case 'wc':
        return 'text-greenBright';
      case 'w':
        return 'text-purpleBright';
      case 'c':
        return 'text-orangeBright';
      default:
        return '';
    }
  };

  const percentageTextColor = (value) => {
    if (value > 0) return 'text-yellowMedium';
    if (value < 0) return 'text-redSoft';
    return '';
  };

  const balancingSuggestionTotals = useMemo(() => {
    return getBalancingSuggestionTotals(data.investmentFundPositions || []);
  }, [data.investmentFundPositions]);

  const allFundIds = useMemo(() => {
    return getAllFundSelectableIds(data.investmentFundPositions || []);
  }, [data.investmentFundPositions]);

  const allFundsSelected = allFundIds.length > 0 && allFundIds.every((id) => selectedFundIds.includes(id));

  const someFundsSelected = allFundIds.some((id) => selectedFundIds.includes(id)) && !allFundsSelected;

  const toggleFundSelection = (positionId) => {
    const id = String(positionId);

    setSelectedFundIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((selectedId) => selectedId !== id);
      }

      return [...prev, id];
    });
  };

  const toggleAllFundSelections = () => {
    setSelectedFundIds((prev) => {
      const allSelected = allFundIds.length > 0 && allFundIds.every((id) => prev.includes(id));

      if (allSelected) {
        return prev.filter((id) => !allFundIds.includes(id));
      }

      return [...new Set([...prev, ...allFundIds])];
    });
  };

  const handleToggleFundLock = async (item) => {
    const id = String(item.id);

    if (lockingFundIdsRef.current.has(id)) {
      return;
    }

    const currentlyLocked = lockedFundIds.includes(id);
    const nextLocked = !currentlyLocked;

    lockingFundIdsRef.current.add(id);
    setLockingFundIds((prev) => [...new Set([...prev, id])]);

    setLockedFundIds((prev) => {
      if (nextLocked) {
        return [...new Set([...prev, id])];
      }

      return prev.filter((lockedId) => lockedId !== id);
    });

    try {
      const res = await fetchWithCsrf(
        `/app/analyst/client/${apiClientId}/dashboard/investment_funds/${item.fund.id}/lock`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            locked: nextLocked,
          }),
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        }
      );

      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.message || 'Erro ao trancar/destrancar fundo');
      }

      addToast({
        type: 'success',
        message: nextLocked ? 'Fundo trancado com sucesso.' : 'Fundo destrancado com sucesso.',
      });

      fetchData();

      if (onPositionUpdated) {
        onPositionUpdated();
      }
    } catch (err) {
      setLockedFundIds((prev) => {
        if (currentlyLocked) {
          return [...new Set([...prev, id])];
        }

        return prev.filter((lockedId) => lockedId !== id);
      });

      addToast({
        type: 'error',
        message: err.message || 'Erro ao atualizar trava do fundo',
      });
    } finally {
      lockingFundIdsRef.current.delete(id);
      setLockingFundIds((prev) => prev.filter((lockingId) => lockingId !== id));
    }
  };

  const showBalanceToast = (message) => {
    if (!message) return;

    addToast({
      type: message.type === 'error' ? 'error' : 'warning',
      message: message.text,
    });
  };

  const handleBalanceInputChange = (id, value) => {
    const numericValue = Number(value) || 0;

    setBalanceInputValues((prev) => {
      const result = getButtonAdjustedValue({
        value: numericValue,
        currentId: id,
        inputValues: prev,
        walletBalance: data.walletBalance,
        typing: true,
      });

      showBalanceToast(result.message);

      return {
        ...prev,
        [String(id)]: numericValue,
      };
    });
  };

  const applyBalanceInputValue = ({ id, value, max = false, fullSell = false }) => {
    setBalanceInputValues((prev) => {
      const result = getButtonAdjustedValue({
        value,
        currentId: id,
        inputValues: prev,
        walletBalance: data.walletBalance,
        max,
        fullSell,
      });

      showBalanceToast(result.message);

      return {
        ...prev,
        [String(id)]: result.value,
      };
    });
  };

  const handleAddBalancing = (item) => {
    applyBalanceInputValue({
      id: item.id,
      value: item.balancing,
    });
  };

  const handleMaxBalancing = (item) => {
    applyBalanceInputValue({
      id: item.id,
      value: Number.POSITIVE_INFINITY,
      max: true,
    });
  };

  const handleFullSell = (item) => {
    applyBalanceInputValue({
      id: item.id,
      value: item.amount,
      fullSell: true,
    });
  };

  const handleExportXlsx = () => {
    const result = exportInvestmentFundsXlsx({
      positions: sortedInvestmentFundPositions,
      balanceInputValues,
      accountNumber: data.accountNumber,
      fileName: `balanceamento_${data.clientName.trim().split(/\s+/).join('_')}_${data.accountNumber}_${new Date()
        .toISOString()
        .slice(0, 10)
        .split('-')
        .reverse()
        .join('-')}.xlsx`,
    });

    addToast({
      type: result.exported ? 'success' : 'error',
      message: result.message,
    });
  };

  const resetSuggestions = () => {
    const positions = data.investmentFundPositions || [];

    setSelectedFundIds(getAllFundSelectableIds(positions));
    setLockedFundIds(getInitialLockedFundIds(positions));
    setLockingFundIds([]);
    lockingFundIdsRef.current.clear();
    setBalanceInputValues({});

    addToast({
      type: 'success',
      message: 'Sugestões limpas com sucesso.',
    });
  };

  const summaryRowIsBlue = sortedInvestmentFundPositions.length % 2 === 0;

  return (
    <div className="bg-white rounded-custom20 p-4 h-full flex flex-col gap-5 ">
      <Title count={data.investmentFundPositionsCount} />

      <div className="grid grid-cols-3 gap-5">
        <Info
          title="Total em Fundos"
          value={data.totalInFunds || 0}
          percentage={totalInFundsPercentage}
          circleClassName={totalMatchesIdeal ? 'bg-blueWhisper text-tealBase' : 'bg-redExtraLight2 text-redBase'}
          tooltip={'Valores calculados com base no patrimônio total excluindo a previdência.'}
        />

        <Info
          title="Ideal em Fundos"
          value={data.idealInFunds || 0}
          percentage={idealInFundsPercentage}
          circleClassName="bg-blueWhisper text-tealBase"
          tooltip={'Valores calculados com base no patrimônio total excluindo a previdência.'}
        />

        <Info
          title="Fundos em Trânsito"
          value={data.pendingSettlements || 0}
          icon={<RefreshCw size={32} strokeWidth={1.5} />}
          circleClassName="bg-white text-blueHighlight"
        />
      </div>

      <div className="px-2">
        <div
          className="grid items-center gap-4 text-blueDarker text-base font-semibold px-1"
          style={{ gridTemplateColumns: BASE_GRID }}
        >
          <div className="truncate">
            <CustomCheckbox
              checked={allFundsSelected}
              indeterminate={someFundsSelected}
              onChange={toggleAllFundSelections}
            />
          </div>

          <SortableHeader field="name" label="Ativo" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />

          <span className="flex items-center gap-2">
            <SortableHeader
              field="pl"
              label="Valor Investido"
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
            />
            <Tooltip
              content={
                'Porcentagem calculada com base no valor corrigido do patrimônio de investimento LIVRE, com exceção do fundo "BTG Tesouro Selic FIRF Ref DI" que utiliza o valor total do patrimônio.'
              }
              width={'w-72'}
            />
          </span>

          <span className="flex items-center gap-2">
            <SortableHeader
              field="idealAmount"
              label="Valor Ideal"
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
            />
            <Tooltip
              content={
                'Porcentagem calculada com base no valor corrigido do patrimônio de investimento LIVRE, com exceção do fundo "BTG Tesouro Selic FIRF Ref DI" que utiliza o valor total do patrimônio.'
              }
              width={'w-72'}
            />
          </span>

          <div className="flex items-center gap-2 min-w-0">
            <SortableHeader
              field="balancing"
              label="Balanceamento"
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
            />
            <Tooltip
              content={
                'Porcentagem calculada com base no valor corrigido do patrimônio de investimento LIVRE, com exceção do fundo "BTG Tesouro Selic FIRF Ref DI" que utiliza o valor total do patrimônio.'
              }
              width={'w-72'}
            />
          </div>

          <div className="truncate">Ajuste de Balanceamento (R$)</div>
        </div>
      </div>

      <div className="space-y-0">
        {sortedInvestmentFundPositions.map((position, idx) => (
          <InvestmentFundRow
            key={position.id}
            position={position}
            idx={idx}
            selectedFundIds={selectedFundIds}
            balanceInputValues={balanceInputValues}
            lockedFundIds={lockedFundIds}
            lockingFundIds={lockingFundIds}
            onToggleFundSelection={toggleFundSelection}
            onBalanceInputChange={handleBalanceInputChange}
            onAddBalancing={handleAddBalancing}
            onMaxBalancing={handleMaxBalancing}
            onFullSell={handleFullSell}
            onToggleFundLock={handleToggleFundLock}
            situationColor={situationColor}
            percentageTextColor={percentageTextColor}
          />
        ))}

        {sortedInvestmentFundPositions.length > 0 && (
          <div className={`${summaryRowIsBlue ? 'bg-blueLight' : 'bg-white'} rounded-xl`}>
            <div
              className="grid items-center gap-4 px-3 py-4 text-sm text-blueDarker"
              style={{ gridTemplateColumns: BASE_SUMMARY_GRID }}
            >
              <div />
              <div />

              <div className="font-semibold text-base text-right">Total sugerido para venda:</div>

              <div className="flex items-center gap-2 min-w-0">
                <BalancingBadge value={balancingSuggestionTotals.totalSuggestedSell} />
              </div>

              <div className="font-semibold text-base text-right">Total sugerido para compra:</div>

              <div className="flex items-center gap-2 min-w-0">
                <BalancingBadge value={balancingSuggestionTotals.totalSuggestedBuy} />
              </div>
            </div>
          </div>
        )}

        {sortedInvestmentFundPositions.length === 0 ? (
          <div className="bg-white rounded-custom20 p-6 text-center text-blueDarker/70">Nenhum fundo encontrado.</div>
        ) : null}
      </div>

      <div className="flex gap-3 justify-end">
        <CustomButton
          onClick={resetSuggestions}
          fontSize="text-sm"
          bgColor="bg-blueUltraLight"
          borderColor="border-graySemiDark"
          className="text-grayDark"
        >
          LIMPAR SUGESTÕES
        </CustomButton>

        <CustomButton onClick={handleExportXlsx} fontSize="text-sm">
          EXPORTAR PLANILHA
        </CustomButton>
      </div>
    </div>
  );
};

export default InvestmentFunds;
